/** @format */
import { css, keyframes } from 'styled-components';

import { quietText, quietTextSmall } from '../../../typography';

/**
 * The tick opening a space for itself beside the label, overshooting its width a
 * little before settling — which both eases the label across and gives the pop.
 */
const openTick = keyframes`
    from {
        width: 0;
        margin-left: 0;
        opacity: 0;
    }

    60% {
        width: 1.15em;
        margin-left: var(--layout-spacing-4);
        opacity: 1;
    }

    to {
        width: 1em;
        margin-left: var(--layout-spacing-4);
        opacity: 1;
    }
`;

/**
 * The stroke of the tick being drawn on, rather than the whole shape simply
 * arriving at once.
 */
const drawTick = keyframes`
    to {
        stroke-dashoffset: 0;
    }
`;

/**
 * A press giving way under the pointer, so something happens on the way down as
 * well as after.
 */
const press = keyframes`
    50% {
        transform: scale(0.94);
    }
`;

export const styles = css`
    /*
     * Stacked rather than in a row, so the question turning into the thanks — two
     * lines of very different length — cannot drag the buttons sideways with it.
     */
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--layout-spacing-8);
    margin-top: var(--layout-spacing-32);
    text-align: center;

    .journey-feedback-question {
        ${quietText}
        color: var(--colors-text-secondary);
    }

    .journey-feedback-beta {
        /*
         * The one place this experiment says out loud that it is one, so it is
         * picked out in the brand pink rather than left to read as body copy.
         */
        color: var(--colors-brand-secondary);
        text-transform: uppercase;
    }

    .journey-feedback-actions {
        display: flex;
        gap: var(--layout-spacing-8);
    }

    .journey-feedback-disclaimer {
        ${quietTextSmall}
        color: var(--colors-text-tertiary);
    }

    .journey-feedback-button {
        ${quietText}
        /*
         * No gap between the label and the tick: an unpressed button has no tick
         * to leave room for, and a gap would sit there anyway and push the label
         * off its own centre. The tick brings its own margin with it as it opens.
         */
        display: inline-flex;
        align-items: center;
        /*
         * Deep enough to read as a pair of buttons worth pressing. They were set
         * as an aside at the bottom of the section and went unnoticed there, and
         * an answer nobody gives is worth nothing to the reports this section is
         * being judged on. The label is left in the plain weight — the size and
         * the target are what was missing, not emphasis on the words.
         */
        padding: var(--layout-spacing-12) var(--layout-spacing-32);
        border: 0.1rem solid var(--colors-border-primary);
        border-radius: var(--effects-radius-full);
        background: none;
        /* The darkest of the text tokens, despite the name — see typography.ts. */
        color: var(--colors-text-secondary);
        cursor: pointer;
        transition-property: background-color, border-color, opacity;
        transition-duration: var(--animations-duration-fast);
        transition-timing-function: var(--animations-easing-ease-out);

        &:hover {
            border-color: var(--colors-brand-secondary);
            background-color: var(--colors-background-secondary);
        }

        &:active {
            animation: ${press} var(--animations-duration-fast)
                var(--animations-easing-ease-out);
        }
    }

    /*
     * Takes no width at all until pressed, so an unpressed button is exactly its
     * label and the label is centred in it. Pressing opens the tick out, easing
     * the label aside to make room — the shift is the point rather than something
     * to be avoided.
     *
     * Sized in em rather than rem so it tracks the text it sits beside, and reads
     * as punctuation on the label rather than an icon of its own.
     */
    .journey-feedback-tick {
        width: 0;
        height: 1em;
        margin-left: 0;
        flex: 0 0 auto;
        opacity: 0;
        overflow: visible;
    }

    .journey-feedback-tick-mark {
        stroke: var(--colors-brand-secondary);
        /*
         * Comfortably longer than the two segments of the tick, so winding the dash
         * offset back to zero draws the whole stroke on.
         */
        stroke-dasharray: 30;
        stroke-dashoffset: 30;
        transform-origin: center;
    }

    /*
     * The answer is marked by the pink border and the tick, and nothing else is
     * dimmed to make the point: the other button is still a live choice, so it has
     * to go on looking like one.
     */
    .journey-feedback-button[aria-pressed='true'] {
        border-color: var(--colors-brand-secondary);

        .journey-feedback-tick {
            animation: ${openTick} var(--animations-duration-normal)
                var(--animations-easing-ease-out) both;
        }

        .journey-feedback-tick-mark {
            animation: ${drawTick} var(--animations-duration-normal)
                var(--animations-easing-ease-out) both;
        }
    }

    /*
     * No reduced-motion helper exists in this codebase (verified) — this is a
     * raw @media block, following the precedent at fan-card.styles.ts. The end
     * state stays, since the tick is the whole point of the press; only the
     * movement getting there goes away.
     */
    @media (prefers-reduced-motion: reduce) {
        .journey-feedback-button {
            animation: none;
        }

        .journey-feedback-button[aria-pressed='true'] {
            .journey-feedback-tick {
                animation: none;
                width: 1em;
                margin-left: var(--layout-spacing-4);
                opacity: 1;
            }

            .journey-feedback-tick-mark {
                animation: none;
                stroke-dashoffset: 0;
            }
        }
    }
`;
